import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResendToken } from '../hooks/useResendToken';
import { usePasswordReset } from '../hooks/usePasswordReset';
import '../auth.css';
import Header from '../header';

export default function CheckEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(5);
  const [emailType, setEmailType] = useState<string>('verification');
  const { resendToken, resending } = useResendToken();
  const { requestPasswordReset, loading: passwordResetLoading } =
    usePasswordReset();

  // Get email and type from URL parameters if available
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    const typeFromUrl = searchParams.get('type');

    const initializeFromUrl = () => {
      if (emailFromUrl) {
        setEmail(emailFromUrl);
      }

      if (typeFromUrl) {
        setEmailType(typeFromUrl);
      }
    };

    initializeFromUrl();
  }, [searchParams]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  return (
    <div className="min-vh-100 d-flex flex-column">
      <Header />
      <div className="min-vh-100 d-flex flex-column justify-content-center align-items-center text-white">
        <div className="col-md-5 form_card text-center">
          {/* Success Icon */}
          <div className="mb-3">
            <div
              className="mx-auto"
              style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'var(--color1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                fill="white"
                className="bi bi-envelope-check"
                viewBox="0 0 16 16"
              >
                <path d="M2 2a2 2 0 0 0-2 2v8.01A2 2 0 0 0 2 14h5.5a.5.5 0 0 0 0-1H2a1 1 0 0 1-.966-.741l5.64-3.471L8 9.583l7-4.2V8.5a.5.5 0 0 0 1 0V4a2 2 0 0 0-2-2H2Zm3.708 6.208L1 11.105V5.383l4.708 2.825ZM1 4.217V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v.217L9 9.383 1 4.217Z" />
                <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Zm-1.993-1.679a.5.5 0 0 0-.686.172l-1.17 1.95-.547-.547a.5.5 0 0 0-.708.708l.774.773a.75.75 0 0 0 1.174-.144l1.335-2.226a.5.5 0 0 0-.172-.686Z" />
              </svg>
            </div>
          </div>

          <h2 className="mb-2 fw-bold text-primary">{t('check_email.title')}</h2>

          <div className="mb-4 text-primary">
            <p className="mb-3">
              {emailType === 'password-reset'
                ? t('check_email.sent_reset')
                : t('check_email.sent_verification')}
            </p>
            {email && <p className="fw-bold text-warning fs-5">{email}</p>}
          </div>

          <div className="alert alert-info">
            <div className="text-start ">
              <i className="bi bi-info-circle"></i>
              <div className="mt-2">
                <strong>{t('check_email.whats_next')}</strong>
                <ul className="mb-0 mt-2 text-start">
                  <li>{t('check_email.check_inbox')}</li>
                  <li>
                    {emailType === 'password-reset'
                      ? t('check_email.click_reset')
                      : t('check_email.click_verify')}
                  </li>
                  <li>
                    {emailType === 'password-reset'
                      ? t('check_email.auto_reset')
                      : t('check_email.auto_verified')}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-muted mb-3">{t('check_email.no_email')}</p>
            <div className="d-flex w-100 gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  if (email) {
                    if (emailType === 'password-reset') {
                      requestPasswordReset(email);
                    } else {
                      resendToken(email);
                    }
                  }
                }}
                disabled={resending || passwordResetLoading || !email}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                {resending || passwordResetLoading
                  ? t('check_email.sending')
                  : emailType === 'password-reset'
                    ? t('check_email.resend_reset')
                    : t('check_email.resend_verify')}
              </button>

              <Link
                to={
                  emailType === 'password-reset'
                    ? '/reset-password'
                    : '/verify-email'
                }
                className="btn btn-outline-primary"
              >
                <i className="bi bi-key me-2"></i>
                {emailType === 'password-reset'
                  ? t('check_email.enter_reset_token')
                  : t('check_email.enter_token')}
              </Link>
            </div>
          </div>

          <div className="mt-4 pt-3 border-top">
            <small className="text-muted">
              <Link to="/" className="text-href">
                <i className="bi bi-arrow-left me-1"></i>
                {t('check_email.back_login')}
              </Link>
            </small>
          </div>

          {/* Countdown indicator */}
          {countdown > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                {t('check_email.refresh_countdown', { count: countdown })}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
